import React, { useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceDot,
} from 'recharts';
import { OptionsVolumePoint, VolumeMetricKey } from '../types/StockData';

interface OptionsVolumeChartProps {
  data: OptionsVolumePoint[];
  selectedMetric: VolumeMetricKey;
  ticker: string;
  timeframe: string;
  isLoading: boolean;
}

const formatNumber = (value: number): string => {
  if (Math.abs(value) >= 1e9) {
    return `${(value / 1e9).toFixed(1)}B`;
  } else if (Math.abs(value) >= 1e6) {
    return `${(value / 1e6).toFixed(1)}M`;
  } else if (Math.abs(value) >= 1e3) {
    return `${(value / 1e3).toFixed(1)}K`;
  }
  return value.toFixed(0);
};

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric',
    year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
  });
};

const getMetricLabel = (metric: VolumeMetricKey): string => {
  const labels: Record<VolumeMetricKey, string> = {
    call_volume: 'Call Volume',
    put_volume: 'Put Volume',
    call_premium: 'Call Premium',
    put_premium: 'Put Premium',
    net_call_premium: 'Net Call Premium',
    net_put_premium: 'Net Put Premium',
    call_volume_bid_side: 'Call Volume (Bid Side)',
    put_volume_bid_side: 'Put Volume (Bid Side)',
    call_volume_ask_side: 'Call Volume (Ask Side)',
    put_volume_ask_side: 'Put Volume (Ask Side)',
    bearish_premium: 'Bearish Premium',
    bullish_premium: 'Bullish Premium',
    call_open_interest: 'Call Open Interest',
    put_open_interest: 'Put Open Interest',
    avg_7_day_call_volume: '7-Day Avg Call Volume',
    avg_7_day_put_volume: '7-Day Avg Put Volume',
    avg_30_day_call_volume: '30-Day Avg Call Volume',
    avg_30_day_put_volume: '30-Day Avg Put Volume',
    avg_3_day_call_volume: '3-Day Avg Call Volume',
    avg_3_day_put_volume: '3-Day Avg Put Volume',
    // Greek exposure
    callDelta: 'Call Delta Exposure',
    putDelta: 'Put Delta Exposure',
    callGamma: 'Call Gamma Exposure',
    putGamma: 'Put Gamma Exposure',
    callCharm: 'Call Charm Exposure',
    putCharm: 'Put Charm Exposure',
    callVanna: 'Call Vanna Exposure',
    putVanna: 'Put Vanna Exposure',
    // Combined exposure
    netDeltaFlow: 'Net Delta Exposure',
    netGammaFlow: 'Net Gamma Exposure',
    netCharmFlow: 'Net Charm Exposure',
    netVannaFlow: 'Net Vanna Exposure',
  };
  return labels[metric];
};

const getMetricColor = (metric: VolumeMetricKey): string => {
  // Call-related metrics in blue/green
  if (metric.includes('call')) {
    return '#3b82f6'; // blue
  }
  // Put-related metrics in red/orange
  if (metric.includes('put')) {
    return '#ef4444'; // red
  }
  // Combined Greek flows (net)
  if (metric.startsWith('net') && !metric.includes('premium')) {
    return '#8b5cf6'; // purple
  }
  // Premium metrics
  if (metric.includes('premium')) {
    if (metric.includes('bullish')) return '#10b981'; // green
    if (metric.includes('bearish')) return '#f59e0b'; // amber
    if (metric.includes('net_call')) return '#3b82f6'; // blue
    if (metric.includes('net_put')) return '#ef4444'; // red
    return '#8b5cf6'; // purple
  }
  // Open interest
  if (metric.includes('open_interest')) {
    return '#6b7280'; // gray
  }
  return '#3b82f6'; // default blue
};

// Removed legacy CustomTooltip; enhanced tooltip will be defined inside the component where data is available.

export const OptionsVolumeChart: React.FC<OptionsVolumeChartProps> = ({
  data,
  selectedMetric,
  ticker,
  timeframe,
  isLoading,
}) => {
  // UI state for normalization and anomaly method
  const [normalizeMetric, setNormalizeMetric] = useState<boolean>(false);
  type AnomalyMethod = 'robust' | 'zscore';
  const [anomalyMethod, setAnomalyMethod] = useState<AnomalyMethod>('robust');
  if (isLoading) {
    return (
      <div className="bg-gradient-to-br from-slate-50 to-white rounded-xl shadow-xl border border-gray-100 p-6">
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading options volume data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="bg-gradient-to-br from-slate-50 to-white rounded-xl shadow-xl border border-gray-100 p-6">
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <p className="text-gray-600 text-lg">No options volume data available</p>
            <p className="text-gray-400 text-sm mt-2">Try selecting a different timeframe</p>
          </div>
        </div>
      </div>
    );
  }

  const metricColor = getMetricColor(selectedMetric);
  const metricLabel = getMetricLabel(selectedMetric);

  // Helper to compute raw metric value (supports derived combined metrics)
  const computeMetricValue = (d: any, key: VolumeMetricKey): number => {
    switch (key) {
      case 'netDeltaFlow':
        return (Number(d.callDelta ?? 0) + Number(d.putDelta ?? 0));
      case 'netGammaFlow':
        return (Number(d.callGamma ?? 0) + Number(d.putGamma ?? 0));
      case 'netCharmFlow':
        return (Number(d.callCharm ?? 0) + Number(d.putCharm ?? 0));
      case 'netVannaFlow':
        return (Number(d.callVanna ?? 0) + Number(d.putVanna ?? 0));
      default:
        return Number((d as any)[key] ?? 0);
    }
  };

  // Sort data by date to ensure proper chronological order
  const sortedData = [...data].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  
  // Debug: Log the actual dates in the dataset
  console.log('Actual dates in dataset:', sortedData.map(d => d.date));
  
  // Calculate stock price range for better Y-axis scaling
  const stockPrices = sortedData.map(d => d.stockClosing).filter(price => price != null);
  const minPrice = Math.min(...stockPrices);
  const maxPrice = Math.max(...stockPrices);
  const priceRange = maxPrice - minPrice;
  const pricePadding = priceRange * 0.1; // 10% padding
  const stockPriceDomain = [minPrice - pricePadding, maxPrice + pricePadding];

  // Correlation: price returns vs selected metric returns
  const computeReturns = (series: number[]): number[] => {
    const out: number[] = [];
    for (let i = 1; i < series.length; i++) {
      const prev = series[i - 1];
      const curr = series[i];
      if (!isFinite(prev) || prev === 0 || !isFinite(curr)) continue;
      out.push((curr - prev) / Math.abs(prev));
    }
    return out;
  };

  const pearson = (a: number[], b: number[]): number | null => {
    const n = Math.min(a.length, b.length);
    if (n < 3) return null;
    const ax = a.slice(-n);
    const bx = b.slice(-n);
    const mean = (xs: number[]) => xs.reduce((s, v) => s + v, 0) / xs.length;
    const ma = mean(ax), mb = mean(bx);
    let num = 0, da = 0, db = 0;
    for (let i = 0; i < n; i++) {
      const va = ax[i] - ma;
      const vb = bx[i] - mb;
      num += va * vb; da += va * va; db += vb * vb;
    }
    const denom = Math.sqrt(da * db);
    if (denom === 0) return null;
    return num / denom;
  };

  const priceSeries: number[] = sortedData.map((d) => d.stockClosing as number).filter((v) => isFinite(v));
  const metricSeries: number[] = sortedData.map((d) => computeMetricValue(d, selectedMetric)).filter((v) => isFinite(v));
  const corr = pearson(computeReturns(priceSeries), computeReturns(metricSeries));
  const corrText = corr == null ? 'N/A' : (corr >= 0 ? `+${corr.toFixed(2)}` : corr.toFixed(2));
  const corrClass = corr == null ? 'text-gray-700 bg-gray-100 border-gray-200' : (corr >= 0 ? 'text-green-700 bg-green-50 border-green-200' : 'text-red-700 bg-red-50 border-red-200');

  // Rolling correlation trend (window=20) on returns, aligned to dates
  const priceSeriesFull: number[] = sortedData.map((d) => d.stockClosing as number);
  const metricSeriesFull: number[] = sortedData.map((d) => computeMetricValue(d, selectedMetric));

  const computeReturnsAligned = (series: number[]): (number | null)[] => {
    const out: (number | null)[] = new Array(series.length).fill(null);
    for (let i = 1; i < series.length; i++) {
      const prev = series[i - 1];
      const curr = series[i];
      if (isFinite(prev) && prev !== 0 && isFinite(curr)) {
        out[i] = (curr - prev) / Math.abs(prev);
      } else {
        out[i] = null;
      }
    }
    return out;
  };

  const rp = computeReturnsAligned(priceSeriesFull);
  const rm = computeReturnsAligned(metricSeriesFull);
  const rollingWindowRC = 20;
  const rollingCorr: (number | null)[] = new Array(sortedData.length).fill(null);
  for (let i = 0; i < sortedData.length; i++) {
    const start = Math.max(1, i - rollingWindowRC + 1); // returns start from index 1
    const a: number[] = [];
    const b: number[] = [];
    for (let j = start; j <= i; j++) {
      if (rp[j] != null && rm[j] != null) {
        a.push(rp[j] as number);
        b.push(rm[j] as number);
      }
    }
    rollingCorr[i] = a.length >= 3 ? pearson(a, b) : null;
  }
  const rollingSparkData = sortedData.map((d, i) => ({ date: d.date, rc: rollingCorr[i] }));
  const lastRcVal: number | null = (() => {
    for (let i = rollingCorr.length - 1; i >= 0; i--) {
      const v = rollingCorr[i];
      if (v != null && isFinite(v)) return v as number;
    }
    return null;
  })();
  const rollingColor = lastRcVal == null ? '#9ca3af' : (lastRcVal >= 0 ? '#16a34a' : '#ef4444');

  // Anomaly detection for selected metric using rolling median + MAD
  const median = (xs: number[]): number => {
    const arr = xs.filter((v) => isFinite(v)).slice().sort((a, b) => a - b);
    if (arr.length === 0) return 0;
    const mid = Math.floor(arr.length / 2);
    return arr.length % 2 === 0 ? (arr[mid - 1] + arr[mid]) / 2 : arr[mid];
  };
  const mad = (xs: number[], med: number): number => {
    const dev = xs.map((v) => Math.abs(v - med));
    return median(dev);
  };
  const computeRobustFlags = (series: number[], window: number, zThresh: number): boolean[] => {
    const flags = new Array(series.length).fill(false) as boolean[];
    for (let i = 0; i < series.length; i++) {
      const start = Math.max(0, i - window);
      const slice = series.slice(start, i);
      if (slice.length < Math.min(window, 10)) continue;
      const med = median(slice);
      const m = mad(slice, med);
      if (!isFinite(m) || m === 0) continue;
      const zRobust = 0.6745 * ((series[i] - med) / m);
      if (Math.abs(zRobust) >= zThresh) flags[i] = true;
    }
    return flags;
  };
  const seriesAll: number[] = sortedData.map((d) => computeMetricValue(d, selectedMetric));

  // Z-Score alternative
  const computeZScoreFlags = (series: number[], window: number, zThresh: number): boolean[] => {
    const flags = new Array(series.length).fill(false) as boolean[];
    for (let i = 0; i < series.length; i++) {
      const start = Math.max(0, i - window);
      const slice = series.slice(start, i);
      if (slice.length < Math.min(window, 10)) continue;
      const mean = slice.reduce((s, v) => s + (isFinite(v) ? v : 0), 0) / slice.length;
      const variance = slice.reduce((s, v) => s + Math.pow((isFinite(v) ? v : 0) - mean, 2), 0) / slice.length;
      const std = Math.sqrt(variance);
      if (!isFinite(std) || std === 0) continue;
      const z = (series[i] - mean) / std;
      if (Math.abs(z) >= zThresh) flags[i] = true;
    }
    return flags;
  };

  // Build display data with optional normalization (% from first non-zero)
  const baseVal = (() => {
    const vals = sortedData.map((d) => computeMetricValue(d, selectedMetric)).filter((v) => isFinite(v));
    const nz = vals.find((v) => v !== 0);
    return (nz ?? vals[0] ?? 1) || 1;
  })();
  const displayData = sortedData.map((d) => {
    const raw = computeMetricValue(d, selectedMetric);
    return {
      ...d,
      metricDisplay: normalizeMetric ? (((raw / baseVal) - 1) * 100) : raw,
    };
  });

  // Choose anomaly series based on method and normalization
  const seriesForAnom: number[] = displayData.map((d) => d.metricDisplay as number);
  const windowN = 20;
  const zThresh = anomalyMethod === 'robust' ? 3.5 : 2.0;
  const anomalyFlagsFinal: boolean[] = anomalyMethod === 'robust'
    ? computeRobustFlags(seriesForAnom, windowN, zThresh)
    : computeZScoreFlags(seriesForAnom, windowN, zThresh);

  // Enhanced tooltip with anomaly indication
  const CustomTooltipEnhanced = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const idx = sortedData.findIndex((d) => d.date === label);
      const isAnomaly = idx >= 0 ? anomalyFlagsFinal[idx] : false;
      return (
        <div className="bg-white p-4 rounded-xl shadow-2xl border border-gray-200 backdrop-blur-sm">
          <p className="font-semibold text-gray-900 mb-2">
            {label}
          </p>
          <div className="space-y-1">
            {payload.map((entry: any, index: number) => (
              <div key={index} className="flex items-center justify-between">
                <span className="text-sm text-gray-600">
                  {entry.dataKey === 'stockClosing' ? 'Stock Price' : metricLabel}:
                </span>
                <span className="font-semibold ml-2" style={{ color: entry.color }}>
                  {entry.dataKey === 'stockClosing'
                    ? `$${Number(entry.value).toFixed(2)}`
                    : (normalizeMetric ? `${Number(entry.value).toFixed(2)}%` : formatNumber(entry.value as number))}
                </span>
              </div>
            ))}
            {isAnomaly && (
              <div className="flex items-center gap-2 mt-1">
                <span className="inline-block w-2 h-2 rounded-full bg-red-500"></span>
                <span className="text-xs font-semibold text-red-600">Anomaly detected in {metricLabel}</span>
              </div>
            )}
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-gradient-to-br from-slate-50 to-white rounded-xl shadow-xl border border-gray-100 p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-xl font-bold text-gray-900 tracking-tight">
            {ticker.toUpperCase()} Options Volume Over Time
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            {metricLabel} • {timeframe} • {sortedData.length} data points
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className={`px-2 py-1 text-xs font-semibold rounded-lg border ${corrClass}`} title="Correlation of price vs metric returns">
            Corr: {corrText}
          </span>
          {/* Rolling correlation sparkline (20 periods) */}
          <div className="hidden sm:flex items-center gap-2" title="Rolling correlation (20)">
            <span className="text-[10px] text-gray-500">20d</span>
            <div className="w-40 h-10">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={rollingSparkData} margin={{ top: 6, right: 8, bottom: 2, left: 8 }}>
                  <XAxis dataKey="date" hide />
                  <YAxis domain={[-1, 1]} hide />
                  <Line type="monotone" dataKey="rc" stroke={rollingColor} strokeWidth={2} dot={false} isAnimationActive={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
          <label className="inline-flex items-center gap-2 text-sm text-gray-700">
            Anomaly:
            <select
              value={anomalyMethod}
              onChange={(e) => setAnomalyMethod(e.target.value as AnomalyMethod)}
              className="px-2 py-1 text-xs font-medium border border-gray-300 rounded bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="robust">Robust</option>
              <option value="zscore">Z-Score</option>
            </select>
          </label>
          <label className="inline-flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={normalizeMetric}
              onChange={(e) => setNormalizeMetric(e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            Normalize metric
          </label>
        </div>
      </div>

      <div className="h-[500px]">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={displayData}
            margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
          >
            <defs>
              <linearGradient id={`gradient-${selectedMetric}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={metricColor} stopOpacity={0.1}/>
                <stop offset="95%" stopColor={metricColor} stopOpacity={0.01}/>
              </linearGradient>
            </defs>
            
            <CartesianGrid 
              strokeDasharray="3 3" 
              stroke="#e5e7eb" 
              opacity={0.6}
            />
            
            <XAxis
              dataKey="date"
              stroke="#6b7280"
              fontSize={12}
              fontWeight={500}
              angle={-45}
              textAnchor="end"
              height={60}
              type="category"
            />
            
            <YAxis
              yAxisId="left"
              tickFormatter={(v) => normalizeMetric ? `${Number(v).toFixed(2)}%` : formatNumber(v as number)}
              stroke={metricColor}
              fontSize={12}
              fontWeight={600}
              strokeWidth={2}
            />
            
            <YAxis
              yAxisId="right"
              orientation="right"
              domain={stockPriceDomain}
              tickFormatter={(value) => `$${value.toFixed(2)}`}
              stroke="#10b981"
              fontSize={12}
              fontWeight={600}
              strokeWidth={2}
            />
            
            <Tooltip content={<CustomTooltipEnhanced />} />
            
            <Line
              yAxisId="left"
              type="monotone"
              dataKey={'metricDisplay'}
              stroke={metricColor}
              strokeWidth={3}
              dot={{ r: 4, fill: metricColor, strokeWidth: 2, stroke: '#fff' }}
              activeDot={{ r: 6, fill: metricColor, strokeWidth: 2, stroke: '#fff' }}
              fill={`url(#gradient-${selectedMetric})`}
            />

            {/* Anomaly markers for the selected metric */}
            {displayData.map((d, i) => (
              anomalyFlagsFinal[i] ? (
                <ReferenceDot
                  key={`anom-${i}`}
                  x={d.date}
                  y={(d as any).metricDisplay as number}
                  yAxisId="left"
                  r={4}
                  fill="#ef4444"
                  stroke="#ffffff"
                  strokeWidth={1}
                />
              ) : null
            ))}
            
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="stockClosing"
              stroke="#10b981"
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4, fill: '#10b981', strokeWidth: 2, stroke: '#fff' }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};
